import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, track } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, Smartphone, CreditCard, Building2 } from "lucide-react";

const INCLUDES = [
  "Detailed timeline", "Relevant astrological periods", "Career & income themes",
  "Key periods to pay attention to", "Personalized interpretation", "Practical reflection prompts",
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Payment() {
  const { readingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | processing | failed

  useEffect(() => {
    api.get(`/readings/${readingId}`).then(({ data }) => {
      setR(data);
      if (data.paid) navigate(`/reading/${readingId}`, { replace: true });
    }).catch(() => navigate("/"));
  }, [readingId]); // eslint-disable-line

  const onSuccess = async (verifyBody) => {
    try {
      await api.post("/payments/verify", { reading_id: readingId, ...verifyBody });
      track("payment_completed", { reading_id: readingId, amount: r.price });
      navigate(`/unlock/${readingId}`, { replace: true });
    } catch (e) {
      setStatus("failed");
      toast.error("Payment verification failed.");
    }
  };

  const pay = async () => {
    setStatus("processing");
    track("checkout_started", { reading_id: readingId, amount: r.price });
    try {
      const { data: order } = await api.post("/payments/create-order", { reading_id: readingId });
      if (order.already_paid) return navigate(`/reading/${readingId}`, { replace: true });

      if (!order.live) {
        // Test mode (no Razorpay keys configured) — simulate a successful capture.
        setTimeout(() => onSuccess({}), 1200);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { setStatus("failed"); return toast.error("Could not load payment gateway."); }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Jaanlo",
        description: order.tool_title,
        order_id: order.order_id,
        prefill: { name: r.name || "", email: user?.email || "", contact: user?.phone || "" },
        theme: { color: "#D4AF37" },
        handler: (resp) => onSuccess({
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_signature: resp.razorpay_signature,
        }),
        modal: { ondismiss: () => setStatus("failed") },
      });
      rzp.on("payment.failed", () => { setStatus("failed"); toast.error("Payment failed. Please try again."); });
      rzp.open();
    } catch (e) {
      setStatus("failed");
      toast.error("Something went wrong starting the payment.");
    }
  };

  if (!r) return <div className="mx-auto max-w-md px-5 py-20"><div className="h-80 rounded-2xl bg-muted shimmer" /></div>;

  return (
    <div className="mx-auto max-w-md px-5 pt-8 md:pt-14">
      <h1 className="font-display text-3xl font-semibold tracking-tight" data-testid="payment-title">Unlock your {r.category.toLowerCase()} timeline</h1>
      <div className="mt-4 flex items-end gap-2">
        <span className="font-display text-5xl font-semibold">₹{r.price}</span>
        <span className="mb-1 text-sm text-muted-foreground">one-time</span>
      </div>

      <div className="card-soft mt-8 rounded-2xl p-6">
        <div className="text-sm font-medium">Includes</div>
        <div className="mt-4 space-y-2.5">
          {INCLUDES.map((i) => (
            <div key={i} className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 gold-text" /> {i}</div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> UPI</span>
        <span className="flex items-center gap-1.5"><CreditCard className="h-4 w-4" /> Cards</span>
        <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> Netbanking</span>
      </div>

      {status === "failed" && (
        <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive" data-testid="payment-failed">
          Payment was not completed. You can try again — you won't be charged twice.
        </div>
      )}

      <Button data-testid="pay-btn" onClick={pay} disabled={status === "processing"}
        className="mt-6 w-full rounded-full bg-foreground py-6 text-base text-background btn-lift hover:bg-foreground">
        {status === "processing" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</> : `Pay ₹${r.price}`}
      </Button>
      {!r.payments_live && (
        <p className="mt-2 text-center text-xs text-muted-foreground">Test mode — no real charge. Add Razorpay keys to go live.</p>
      )}
      <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Secure payment via Razorpay • No subscription
      </p>
    </div>
  );
}
